// imageUploadService.js
//
// Build 28: one place for "validate → compress → upload to Storage →
// return URL". Extracted from the proven defect-image flow in App.js so
// Notes, Diagrams, and any future caller share it instead of each
// re-implementing the same three steps. Defects can migrate to this
// later without behavior change.
//
// Reuses the EXISTING utilities — does not re-implement compression or
// validation:
//   - validateImageFile  (utils/imageCompression.js)
//   - compressImage       (utils/imageCompression.js)  — 500KB ceiling
//
// Storage layout: <scope>/<ownerId>/<timestamp>_<sanitized-name>

import { ref, uploadBytes, getDownloadURL, getBlob } from 'firebase/storage';
import { storage } from '../firebase';
import { validateImageFile, compressImage } from '../utils/imageCompression';

/** Strip characters that are awkward in Storage object names. */
function sanitizeName(name) {
  return (name || 'image')
    .replace(/[^\w.\-]+/g, '_')
    .slice(0, 80);
}

/**
 * Validate, compress, and upload an image. Returns the download URL.
 *
 * @param {File}   file      the raw File from an <input type=file>
 * @param {Object} opts
 * @param {string} opts.scope    top-level Storage folder, e.g. 'notes' | 'diagrams'
 * @param {string} opts.ownerId  the owning user/entity id (keeps paths tidy + scoped)
 * @returns {Promise<string>} download URL
 * @throws  {Error} with a user-friendly message if validation fails
 */
export async function uploadImage(file, { scope, ownerId }) {
  const check = validateImageFile(file);
  if (!check.valid) {
    throw new Error(check.error || 'Invalid image file');
  }

  // Reuse the shared 500KB-ceiling compressor (Build 24.0.4 tuning).
  const compressed = await compressImage(file);

  const safeScope = scope || 'misc';
  const safeOwner = ownerId || 'anonymous';
  const path = `${safeScope}/${safeOwner}/${Date.now()}_${sanitizeName(file.name)}`;

  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, compressed);
  return getDownloadURL(storageRef);
}

/**
 * Build 28 (item 2): upload a base64 data URL and return the Storage
 * download URL. Excalidraw stores image binaries as data URLs (not
 * File objects), so whiteboard image offloading needs this variant.
 * Skips compression — Excalidraw images are already managed by it and
 * dataURLs aren't File-shaped; we upload the bytes as-is.
 *
 * @param {string} dataUrl   "data:image/png;base64,...."
 * @param {Object} opts      { scope, ownerId, name? }
 * @returns {Promise<string>} download URL
 */
export async function uploadDataUrl(dataUrl, { scope, ownerId, name }) {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    throw new Error('Not a data URL');
  }
  const res = await fetch(dataUrl);
  const blob = await res.blob();

  const safeScope = scope || 'misc';
  const safeOwner = ownerId || 'anonymous';
  const ext = (blob.type && blob.type.split('/')[1]) || 'png';
  const path = `${safeScope}/${safeOwner}/${Date.now()}_${sanitizeName(name || 'img')}.${ext}`;

  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}

/**
 * Build 28 (item 2): fetch a Storage (or any) URL and convert it back
 * to a base64 data URL — needed to re-hydrate Excalidraw's files map on
 * load, since Excalidraw's addFiles() expects dataURL entries.
 *
 * v1.0.3 fix: bare fetch() against Firebase Storage URLs was being
 * blocked by CORS (Storage doesn't ship with permissive CORS headers
 * by default). The browser blocked the response → urlToDataUrl threw
 * → the rehydrate effect swallowed it → Excalidraw rendered a broken
 * placeholder icon. We now try the Firebase Storage SDK's getBlob()
 * first, which uses the SDK's own authorized channel and bypasses
 * browser CORS entirely. Falls back to bare fetch for any URL that
 * isn't a Firebase Storage one.
 *
 * @param {string} url
 * @returns {Promise<string>} data URL
 */
export async function urlToDataUrl(url) {
  let blob;
  let strategy = 'sdk-getBlob';
  try {
    // ref(storage, httpUrl) accepts a download URL and returns a Ref
    // pointing at the same object; getBlob then fetches via the SDK's
    // authorized channel, no CORS preflight needed.
    const r = ref(storage, url);
    blob = await getBlob(r);
  } catch (e) {
    // SDK path failed — log loudly so the failure isn't silent, then
    // try plain fetch as a fallback.
    console.warn('[imageUploadService] getBlob failed, falling back to fetch:', e?.code || e?.message || e);
    strategy = 'fetch-fallback';
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fetch HTTP ${res.status}`);
      blob = await res.blob();
    } catch (e2) {
      console.error('[imageUploadService] both getBlob and fetch failed for', url, e2);
      throw e2;
    }
  }
  if (!blob || blob.size === 0) {
    console.error('[imageUploadService] empty blob from', strategy, 'for', url);
    throw new Error('Empty image data');
  }
  console.log(`[imageUploadService] loaded ${blob.size} bytes via ${strategy} (${blob.type || 'no mime'})`);
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
